import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import type { StaticImageData } from "next/image";
import apiError from "./images/api-error.jpg";
import brokenZip from "./images/broken-zip.jpg";
import crashReport from "./images/crash-report.jpg";
import duplicateCharge from "./images/duplicate-charge.jpg";
import jacketBlack from "./images/jacket-black.jpg";
import jacketBlue from "./images/jacket-blue.jpg";

export type Industry = "ecommerce" | "technology" | "gaming" | "fintech";

export type DemoPlacement = "solutions" | "home";

export type TicketId =
  | "ecommerce_product"
  | "ecommerce_orders"
  | "ecommerce_returns"
  | "ecommerce_promos"
  | "technology_api"
  | "technology_billing"
  | "technology_bug"
  | "technology_sso"
  | "gaming_account"
  | "gaming_purchase"
  | "gaming_crash"
  | "gaming_ban"
  | "fintech_card"
  | "fintech_dispute"
  | "fintech_fraud"
  | "fintech_statement";

export type DemoAttachment =
  | { type: "image"; name: string; src: StaticImageData }
  | { type: "pdf"; name: string; size: string };

export type DemoMessage = {
  from: "customer" | "fin";
  text: MessageDescriptor;
  attachment?: DemoAttachment;
  /** A link Fin shares, shown under the message text. */
  link?: string;
};

export type DemoProduct = {
  name: string;
  price: MessageDescriptor;
  description: MessageDescriptor;
  cta: MessageDescriptor;
  /** The shopper already added it, so the CTA shows its done state. */
  added?: boolean;
  image: StaticImageData;
};

export type DemoTicket = {
  id: TicketId;
  /** The business whose Messenger this is; Fin introduces itself as theirs. */
  company: string;
  name: string;
  minutesAgo: number;
  /** Shown in the inbox list under the name. */
  preview: MessageDescriptor;
  messages: DemoMessage[];
  /** Rendered inside the Fin message they follow. */
  products?: { afterMessage: number; items: DemoProduct[] };
  /** Opens straight on the customer's question, without Fin's greeting. */
  hideGreeting?: boolean;
  system: MessageDescriptor;
};

const customer = (
  text: MessageDescriptor,
  attachment?: DemoAttachment,
): DemoMessage => ({ from: "customer", text, attachment });
const fin = (
  text: MessageDescriptor,
  link?: string,
  attachment?: DemoAttachment,
): DemoMessage => ({ from: "fin", text, link, attachment });

const resolved = msg`Fin resolved this conversation`;

const ECOMMERCE: DemoTicket[] = [
  {
    id: "ecommerce_product",
    company: "Ridgeway Outdoors",
    name: "Jess Park",
    minutesAgo: 2,
    preview: msg`Product recommendation`,
    hideGreeting: true,
    messages: [
      customer(msg`Hi! I need a rain jacket for Scotland.`),
      fin(msg`Nice. Day hikes, or multi-day trips?`),
      customer(msg`Day hikes, mostly.`),
      fin(msg`Then these two are our bestsellers.`),
      fin(msg`Here’s your checkout link:`, "ridgewayoutdoors.com/checkout"),
      customer(msg`Just ordered it, thanks!`),
    ],
    products: {
      afterMessage: 3,
      items: [
        {
          name: "StormShell Lite Jacket",
          price: msg`$129`,
          description: msg`Ultralight, waterproof, packs into its own pocket.`,
          cta: msg`Added to cart`,
          added: true,
          image: jacketBlue,
        },
        {
          name: "SummitShield Hybrid",
          price: msg`$169`,
          description: msg`Waterproof with light insulation for colder days.`,
          cta: msg`Add to cart`,
          image: jacketBlack,
        },
      ],
    },
    system: resolved,
  },
  {
    id: "ecommerce_orders",
    company: "Ridgeway Outdoors",
    name: "Liam Hart",
    minutesAgo: 6,
    preview: msg`Delayed delivery`,
    messages: [
      customer(
        msg`My order was meant to arrive on Monday and the tracking hasn’t changed since last Thursday.`,
      ),
      fin(
        msg`Sorry about that. Order #48213 has been at the courier’s Leeds depot since Thursday with no scans since, so it’s likely been lost.`,
      ),
      customer(msg`It’s a birthday present. It needs to be here by Saturday.`),
      fin(
        msg`I can send a free replacement by express delivery. If it ships today, the courier estimates Friday. Shall I go ahead?`,
      ),
      customer(msg`Yes please.`),
      fin(
        msg`Done. Your replacement ships today, and I’ve emailed you the new tracking link.`,
      ),
      fin(
        msg`I’ve also reported the original parcel as lost. If it does turn up, you can send it back for free.`,
      ),
      customer(msg`Thanks, that’s a relief.`),
    ],
    system: resolved,
  },
  {
    id: "ecommerce_returns",
    company: "Ridgeway Outdoors",
    name: "Nina Brooks",
    minutesAgo: 11,
    preview: msg`Damaged item refund`,
    messages: [
      customer(
        msg`My jacket arrived with a broken zip. I’ve only worn it once.`,
      ),
      fin(msg`Sorry about that. Could you send me a photo of the zip?`),
      customer(msg`Here’s a photo.`, {
        type: "image",
        name: "zip.jpg",
        src: brokenZip,
      }),
      fin(
        msg`Thanks. I can see the zip has split below the pull, which is a manufacturing fault, so it’s covered. Would you prefer a replacement or a refund?`,
      ),
      customer(msg`I’d rather have a refund than a replacement.`),
      fin(
        msg`No problem. I’ll refund $180 to the card you paid with and email you a free return label. Shall I go ahead?`,
      ),
      customer(msg`Yes please.`),
      fin(
        msg`Done. Your refund should show in 3–5 business days, and the return label is in your inbox.`,
      ),
    ],
    system: resolved,
  },
  {
    id: "ecommerce_promos",
    company: "Ridgeway Outdoors",
    name: "Omar Ali",
    minutesAgo: 16,
    preview: msg`Promo code issue`,
    messages: [
      customer(
        msg`I’m trying to use SUMMER20 at checkout but it says it’s invalid.`,
      ),
      fin(msg`Sorry, SUMMER20 ended on Sunday, so it can’t be used any more.`),
      fin(
        msg`You do have 500 loyalty points, worth $25 off. Want me to apply them to this order?`,
      ),
      customer(msg`Oh nice, yes please.`),
      fin(msg`Done. $25 off is applied, bringing your total to $96.`),
      customer(msg`Does free shipping still apply?`),
      fin(
        msg`Yes. Orders over $75 ship free, so you’re still covered after the discount.`,
      ),
      customer(msg`Great, checking out now.`),
    ],
    system: resolved,
  },
];

const TECHNOLOGY: DemoTicket[] = [
  {
    id: "technology_api",
    company: "Tidewater Analytics",
    name: "Priya Shah",
    minutesAgo: 3,
    preview: msg`API authentication error`,
    hideGreeting: true,
    messages: [
      customer(
        msg`Our API calls started failing with 401 errors this morning.`,
        { type: "image", name: "error.png", src: apiError },
      ),
      fin(
        msg`Sorry about that. Your workspace rotated its API key at 9:14am, and your requests are still using the old one.`,
      ),
      customer(msg`That was our security audit. Can we get the old key back?`),
      fin(
        msg`Revoked keys can’t be restored, but your new key is ready. Swap it into your environment variables and requests will go through again.`,
        "app.tidewater.io/settings/api-keys",
      ),
      customer(msg`Swapped it in and we’re back up. Thanks!`),
    ],
    system: resolved,
  },
  {
    id: "technology_billing",
    company: "Tidewater Analytics",
    name: "Marcus Webb",
    minutesAgo: 7,
    preview: msg`Adding seats`,
    messages: [
      customer(
        msg`We’re hiring five engineers next week. How much would it cost to add them?`,
      ),
      fin(
        msg`You’re on the Team plan at $40 per seat a month. Five more seats would be a prorated $113 for the rest of this billing cycle, then $200 a month after that.`,
      ),
      customer(msg`Can we add them now so they’re ready on Monday?`),
      fin(
        msg`Sure. I’ll add five seats and charge $113 to the card ending 1881. Shall I go ahead?`,
      ),
      customer(msg`Yes please.`),
      fin(
        msg`Done. The seats are ready, and here’s your updated invoice. You can invite your new engineers from Settings > Team.`,
        undefined,
        { type: "pdf", name: "Invoice-TW-1042.pdf", size: "84 KB" },
      ),
      customer(msg`Perfect, thanks.`),
    ],
    system: resolved,
  },
  {
    id: "technology_bug",
    company: "Tidewater Analytics",
    name: "Sofia Reyes",
    minutesAgo: 12,
    preview: msg`CSV export bug`,
    messages: [
      customer(msg`Exporting a dashboard to CSV just gives me an empty file.`),
      fin(msg`Sorry about that. Is it every dashboard, or just one?`),
      customer(msg`Just our Q3 revenue dashboard. The others export fine.`),
      fin(
        msg`Thanks. That dashboard has over 50,000 rows, and there’s a known issue with large exports that our engineers are fixing now.`,
      ),
      fin(
        msg`In the meantime, you can filter it by month and export each month separately.`,
      ),
      customer(msg`I need the full file for a board meeting on Friday.`),
      fin(
        msg`Understood. I’ve passed this to our support engineers with your dashboard details and Friday deadline, so they can send you the full export directly.`,
      ),
    ],
    system: msg`Fin handed this to Tier 2 with a summary`,
  },
  {
    id: "technology_sso",
    company: "Tidewater Analytics",
    name: "Tom Becker",
    minutesAgo: 18,
    preview: msg`SSO setup`,
    messages: [
      customer(msg`How do I set up SSO with Okta?`),
      fin(
        msg`In Okta, add Tidewater from the app catalog and copy the metadata URL it gives you.`,
      ),
      fin(
        msg`Then paste it into Settings > Security > SAML SSO and click Test connection.`,
      ),
      customer(msg`Test passed. Can I make SSO mandatory for everyone?`),
      fin(
        msg`Yes. Turn on Require SSO on the same page. Admins can still sign in with a password, so you won’t get locked out.`,
      ),
      customer(msg`Done, thanks!`),
    ],
    system: resolved,
  },
];

const GAMING: DemoTicket[] = [
  {
    id: "gaming_account",
    company: "Emberfall Studios",
    name: "Ava Lindqvist",
    minutesAgo: 2,
    preview: msg`Locked account`,
    hideGreeting: true,
    messages: [
      customer(
        msg`I can’t log in. It says my account is locked, but I haven’t tried that many times.`,
      ),
      fin(
        msg`Sorry about that. There were 14 failed sign-ins from a new device overnight, so we locked your account to protect it.`,
      ),
      customer(msg`That wasn’t me. Is my stuff safe?`),
      fin(
        msg`Yes. Nothing was bought or traded, and your inventory is untouched. I’ve emailed you a reset link. Once you’ve set a new password, turn on two-step verification.`,
        "emberfall.gg/account/security",
      ),
      customer(msg`Reset done and two-step is on. Thanks!`),
    ],
    system: resolved,
  },
  {
    id: "gaming_purchase",
    company: "Emberfall Studios",
    name: "Leo Martins",
    minutesAgo: 6,
    preview: msg`Missing purchase`,
    messages: [
      customer(
        msg`I bought 1,200 Crystals an hour ago and they never showed up.`,
      ),
      fin(
        msg`Sorry about that. Your $9.99 payment went through at 4:12pm, but the delivery to your account failed during a server restart.`,
      ),
      fin(
        msg`I’ve added the 1,200 Crystals to your account now. Here’s your receipt.`,
        undefined,
        { type: "pdf", name: "Receipt-EF-58217.pdf", size: "36 KB" },
      ),
      customer(msg`Will I get charged twice?`),
      fin(
        msg`No. You were only charged once, and the receipt shows the single $9.99 payment.`,
      ),
      customer(msg`They’re there now. Thanks!`),
    ],
    system: resolved,
  },
  {
    id: "gaming_crash",
    company: "Emberfall Studios",
    name: "Hana Sato",
    minutesAgo: 11,
    preview: msg`Crash on match load`,
    messages: [
      customer(
        msg`Since yesterday’s update the game crashes every time I load into a match.`,
        { type: "image", name: "crash.png", src: crashReport },
      ),
      fin(
        msg`Sorry about that. That error means your graphics driver stopped responding. Yesterday’s update needs driver version 560 or newer, and you’re on 546.`,
      ),
      customer(msg`How do I update it?`),
      fin(
        msg`Open your graphics card’s app and install the latest driver. Then restart your PC and choose Verify game files in the launcher.`,
      ),
      customer(msg`Updated and it’s working again. Thanks!`),
    ],
    system: resolved,
  },
  {
    id: "gaming_ban",
    company: "Emberfall Studios",
    name: "Jake Morrow",
    minutesAgo: 17,
    preview: msg`Ban appeal`,
    messages: [
      customer(msg`My account got banned for cheating. I’ve never cheated.`),
      fin(
        msg`Sorry to hear that. Your account was suspended yesterday after our anti-cheat flagged a program running alongside the game.`,
      ),
      customer(
        msg`I was only running screen recording software for my stream.`,
      ),
      fin(
        msg`Thanks, that helps. Some recording tools can trigger a false positive.`,
      ),
      fin(
        msg`I’ve sent your appeal to our Trust & Safety team with your match history and recording setup. They review appeals within 48 hours and will email you the outcome.`,
      ),
      customer(msg`Okay, thanks for passing it on.`),
    ],
    system: msg`Fin handed this to Trust & Safety with a summary`,
  },
];

const FINTECH: DemoTicket[] = [
  {
    id: "fintech_card",
    company: "Fernwell",
    name: "Clara Novak",
    minutesAgo: 2,
    preview: msg`Card declined abroad`,
    hideGreeting: true,
    messages: [
      customer(msg`My card keeps getting declined in Lisbon. I’m on holiday.`),
      fin(
        msg`Sorry about that. Your card is fine, but international payments are switched off in your settings, so payments outside the US are blocked.`,
      ),
      customer(msg`Can you turn them on?`),
      fin(
        msg`Sure. I’ll switch on international payments for your card ending 4417. Shall I go ahead?`,
      ),
      customer(msg`Yes please.`),
      fin(
        msg`Done. You can pay in Lisbon now, and you can switch this off again anytime in Card settings.`,
      ),
      customer(msg`Just paid for lunch. Thanks!`),
    ],
    system: resolved,
  },
  {
    id: "fintech_dispute",
    company: "Fernwell",
    name: "Daniel Osei",
    minutesAgo: 6,
    preview: msg`Duplicate charge`,
    messages: [
      customer(msg`I was charged twice for the same hotel booking.`, {
        type: "image",
        name: "transactions.png",
        src: duplicateCharge,
      }),
      fin(
        msg`Sorry about that. I can see two $214.50 payments to Harbor Inn on October 28, and both have settled.`,
      ),
      fin(
        msg`I’ve opened a dispute for the second payment and added a temporary $214.50 credit to your account while the hotel responds.`,
      ),
      customer(msg`How long does that take?`),
      fin(
        msg`Most disputes are settled within 10 business days. If the hotel confirms the duplicate, the credit becomes permanent.`,
      ),
      customer(msg`Great, thanks.`),
    ],
    system: resolved,
  },
  {
    id: "fintech_fraud",
    company: "Fernwell",
    name: "Grace Kim",
    minutesAgo: 11,
    preview: msg`Unrecognized payments`,
    messages: [
      customer(msg`There are three payments on my account I didn’t make.`),
      fin(
        msg`I’ve frozen your card ending 9032 so no more payments can go through. Are these the payments: $49.99, $89.00, and $120.00 to online stores yesterday?`,
      ),
      customer(msg`Yes, none of those were me.`),
      fin(
        msg`Thanks. I’ve ordered you a new card, which should arrive in 3–5 days.`,
      ),
      fin(
        msg`I’ve also passed this to our fraud team with the payment details. They’ll contact you within 24 hours about getting your $258.99 back.`,
      ),
      customer(msg`Thank you, that’s a relief.`),
    ],
    system: msg`Fin handed this to the fraud team with a summary`,
  },
  {
    id: "fintech_statement",
    company: "Fernwell",
    name: "Sam Patel",
    minutesAgo: 17,
    preview: msg`Statements for mortgage`,
    messages: [
      customer(
        msg`I need my last three months of statements for a mortgage application.`,
      ),
      fin(
        msg`Sure. Here’s one PDF covering August to October, with your account details on every page.`,
        undefined,
        { type: "pdf", name: "Statements-Aug-Oct.pdf", size: "212 KB" },
      ),
      customer(msg`Will the lender need anything else from you?`),
      fin(
        msg`Some lenders also ask for a proof of account letter. You can download one anytime from Documents in the app.`,
        "app.fernwell.com/documents",
      ),
      customer(msg`Perfect, thanks.`),
    ],
    system: resolved,
  },
];

export const TICKETS: Record<Industry, DemoTicket[]> = {
  ecommerce: ECOMMERCE,
  technology: TECHNOLOGY,
  gaming: GAMING,
  fintech: FINTECH,
};
